/**
 * tbyb-cart-lock.js
 * ---------------------------------------------------------------------------
 * Locks the quantity of "Try Before You Buy" (trial) products inside the cart.
 *
 * The storefront cart is the Recharge Storefront Experiences drawer/overlay,
 * a React app rendered inside a Shadow DOM, so the theme's Liquid cart can't
 * lock it. We inject a stylesheet INTO each cart shadow root (the same hosts
 * recharge-cart-theme.js styles) that, for trial line items, hides the +/-
 * quantity buttons and disables the quantity input — while leaving the Remove
 * button untouched. CSS injection (adoptedStyleSheets) survives Recharge's
 * re-renders, which per-element JS edits do not. A JS pass + MutationObserver
 * reinforce it and re-apply when the cart re-renders.
 *
 * Trial line item detection (no product link exists in this cart markup):
 *   - the line item's product image alt text contains "tbyb", and/or
 *   - the line item's text content contains "tbyb".
 * The trial products' titles all end in "- TBYB", so this is reliable.
 *
 * UX guard for the cart only; not server-side enforcement.
 */
(function () {
  if (window.tbybEnabled === false) return;

  var CART_HOST_IDS = [
    'recharge-storefront-experiences__cart-overlay',
    'recharge-storefront-experiences__cart-drawer',
  ];

  // Hide the +/- buttons that sit alongside the quantity input, and block
  // interaction with the quantity input, but only inside a trial line item
  // (identified by the product image alt containing "tbyb").
  var CART_CSS = [
    '[role="listitem"]:has(img[alt*="tbyb" i]) div:has(> input[name="quantity"]) > button {',
    '  display: none !important;',
    '}',
    '[role="listitem"]:has(img[alt*="tbyb" i]) input[name="quantity"] {',
    '  pointer-events: none !important;',
    '  user-select: none !important;',
    '}',
  ].join('\n');

  function injectCss(root) {
    // Preferred: adoptedStyleSheets (persists across React re-renders).
    try {
      var sheets = root.adoptedStyleSheets || [];
      for (var i = 0; i < sheets.length; i++) {
        if (sheets[i].__tbybLock) return; // already injected
      }
      var sheet = new CSSStyleSheet();
      sheet.__tbybLock = true;
      sheet.replaceSync(CART_CSS);
      root.adoptedStyleSheets = sheets.concat([sheet]);
      return;
    } catch (e) {
      // Fallback: a <style> tag inside the shadow root.
      var existing = root.querySelector('style[data-tbyb-lock]');
      if (!existing) {
        existing = document.createElement('style');
        existing.setAttribute('data-tbyb-lock', '');
        root.appendChild(existing);
      }
      existing.textContent = CART_CSS;
    }
  }

  // Belt-and-suspenders: also disable the quantity input via JS for trial items,
  // in case :has()/adoptedStyleSheets is unavailable in the browser.
  function reinforce(root) {
    var items = root.querySelectorAll('[role="listitem"]');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!/tbyb/i.test(item.textContent || '')) continue;

      var input = item.querySelector('input[name="quantity"], input[aria-label="Quantity" i]');
      if (input) {
        input.readOnly = true;
        input.setAttribute('aria-disabled', 'true');
        input.style.pointerEvents = 'none';

        // Hide the +/- buttons that share the input's wrapper (Remove is outside it).
        var wrapper = input.parentElement;
        if (wrapper) {
          var buttons = wrapper.querySelectorAll('button');
          for (var b = 0; b < buttons.length; b++) buttons[b].style.display = 'none';
        }
      }
    }
  }

  function applyAll() {
    for (var i = 0; i < CART_HOST_IDS.length; i++) {
      var host = document.getElementById(CART_HOST_IDS[i]);
      if (host && host.shadowRoot) {
        injectCss(host.shadowRoot);
        reinforce(host.shadowRoot);
      }
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    applyAll();

    var observer = new MutationObserver(function () { applyAll(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    ['click', 'recharge:cart:opened', 'cart:open', 'cart:updated', 'cart:change', 'cart:refresh'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(applyAll, 50); }, true);
    });

    window.addEventListener('pageshow', applyAll);
  });
})();


/* =========================================================================
 * TBYB cart guard (real cart enforcement)
 * -------------------------------------------------------------------------
 * The block above is cosmetic only: it hides the quantity widgets inside the
 * Recharge cart. Customers were still ending up with more than one trial unit
 * by re-adding the product, switching the selling plan (which creates a second
 * cart line for the same product) or reloading the page mid-request.
 *
 * The guard below reconciles the actual cart after every cart mutation and
 * clamps every trial product back to MAX_UNITS_PER_TRIAL_PRODUCT. Still a
 * storefront guard rather than server-side enforcement, but it acts on the cart
 * itself instead of only on the UI.
 * ========================================================================= */
(function () {
  'use strict';

  if (window.tbybEnabled === false) return;

  var MAX_UNITS_PER_TRIAL_PRODUCT = 1;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (root.charAt(root.length - 1) !== '/') root += '/';

  var originalFetch = window.fetch.bind(window);
  var CART_WRITE = /\/cart\/(add|change|update)(\.js)?(\?|$)/i;
  var GUARD_HEADER = 'X-TBYB-Guard';

  function isTrialItem(item) {
    var handle = (item && (item.handle || item.product_handle)) || '';
    var title = (item && (item.product_title || item.title)) || '';
    return /tbyb/i.test(handle) || /-\s*tbyb\s*$/i.test(title);
  }

  // Group by product so a second line created by switching selling plan counts
  // against the same allowance as the first one.
  function trialKey(item) {
    var id = (item && (item.handle || item.product_handle || item.product_id || item.product_title)) || '';
    return String(id).toLowerCase();
  }

  function api(path, options) {
    options = options || {};
    var headers = options.headers || {};
    headers['Content-Type'] = 'application/json';
    headers[GUARD_HEADER] = '1';
    options.headers = headers;
    options.credentials = 'same-origin';
    return originalFetch(root + path, options).then(function (res) { return res.json(); });
  }

  function showNote() {
    var notes = document.querySelectorAll('[data-tbyb-note]');
    for (var i = 0; i < notes.length; i++) {
      var note = notes[i];
      note.hidden = false;
      note.classList.remove('tbyb-trial-note--flash');
      void note.offsetWidth;
      note.classList.add('tbyb-trial-note--flash');
    }
  }

  function announce(cart) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart: cart, tbybGuard: true } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { tbybGuard: true } }));
  }

  var running = false;
  var queued = false;

  function reconcile() {
    if (running) { queued = true; return Promise.resolve(); }
    running = true;
    return api('cart.js')
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        var seen = {};
        var updates = {};
        var over = false;
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          if (!isTrialItem(item)) continue;
          var key = trialKey(item);
          var used = seen[key] || 0;
          var allowed = Math.max(0, MAX_UNITS_PER_TRIAL_PRODUCT - used);
          var target = Math.min(item.quantity, allowed);
          seen[key] = used + target;
          if (target !== item.quantity) {
            updates[item.key] = target;
            over = true;
          }
        }
        if (!over) return null;
        return api('cart/update.js', { method: 'POST', body: JSON.stringify({ updates: updates }) });
      })
      .then(function (cart) {
        if (cart) { showNote(); announce(cart); }
      })
      .catch(function () { /* never break the cart because of the guard */ })
      .then(function () {
        running = false;
        if (queued) { queued = false; scheduleReconcile(200); }
      });
  }

  var timer = null;
  function scheduleReconcile(delay) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; reconcile(); }, typeof delay === 'number' ? delay : 120);
  }

  function headerValue(headers, name) {
    if (!headers) return null;
    if (typeof headers.get === 'function') return headers.get(name);
    return headers[name] || headers[name.toLowerCase()] || null;
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isGuard = init && headerValue(init.headers, GUARD_HEADER);
    var watch = !isGuard && CART_WRITE.test(String(url));
    var result = originalFetch(input, init);
    if (watch) { result.then(function () { scheduleReconcile(); }, function () {}); }
    return result;
  };

  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_WRITE.test(String(url))) {
      this.addEventListener('loadend', function () { scheduleReconcile(); });
    }
    return originalOpen.apply(this, arguments);
  };

  function onCartEvent(event) {
    if (event && event.detail && event.detail.tbybGuard) return;
    scheduleReconcile();
  }

  ['cart:updated', 'cart:change', 'cart:refresh', 'cart:open', 'recharge:cart:opened'].forEach(function (name) {
    document.documentElement.addEventListener(name, onCartEvent);
    document.addEventListener(name, onCartEvent);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleReconcile(0); });
  } else {
    scheduleReconcile(0);
  }
  window.addEventListener('pageshow', function () { scheduleReconcile(0); });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) scheduleReconcile(0); });

  window.tbybCartGuard = { reconcile: reconcile };
})();
